import {
  computed,
  defineComponent,
  effectScope,
  nextTick,
  onMounted,
  ref,
  shallowRef,
  watch
} from '@vue/composition-api';
import { useEventListener } from '@vueuse/core';
import { throttle } from 'lodash-es';
import type { PropType } from 'vue';

import { downloadAssetFromUrl } from '../../composables/utils.composable';
import type { IAssetViewerItem } from './asset-viewer.types';

interface AssetViewerMode {
  name: string;
  icon: string;
}

type AssetViewerAction =
  | 'zoomIn'
  | 'zoomOut'
  | 'clockwise'
  | 'anticlockwise'
  | 'download';

const EVENT_CODE = {
  backspace: 'Backspace',
  delete: 'Delete',
  down: 'ArrowDown',
  end: 'End',
  enter: 'Enter',
  esc: 'Escape',
  home: 'Home',
  left: 'ArrowLeft',
  numpadEnter: 'NumpadEnter',
  pageDown: 'PageDown',
  pageUp: 'PageUp',
  right: 'ArrowRight',
  space: 'Space',
  tab: 'Tab',
  up: 'ArrowUp'
};

export default defineComponent({
  emits: ['close', 'switch'],
  name: 'AssetViewer',
  props: {
    closeOnPressEscape: {
      default: true,
      type: Boolean
    },
    hideOnClickModal: {
      default: false,
      type: Boolean
    },
    infinite: {
      default: true,
      type: Boolean
    },
    initialUid: {
      default: 0,
      type: Number
    },
    items: {
      default: () => [],
      type: Array as PropType<Array<IAssetViewerItem>>
    },
    showDownload: {
      default: true,
      type: Boolean
    },
    zoomRate: {
      default: 1.2,
      type: Number
    }
  },
  setup(props, { emit }) {
    const modes: Record<'CONTAIN' | 'ORIGINAL', AssetViewerMode> = {
      CONTAIN: {
        icon: 'mdi-fullscreen',
        name: 'contain'
      },
      ORIGINAL: {
        icon: 'mdi-fullscreen-exit',
        name: 'original'
      }
    };

    const wrapper = ref<HTMLDivElement>();
    const assetRefs = ref<(HTMLImageElement | HTMLVideoElement)[]>([]);

    const scopeEventListener = effectScope();

    const loading = ref(true);
    const mode = shallowRef<AssetViewerMode>(modes.CONTAIN);
    const transform = ref({
      deg: 0,
      enableTransition: false,
      offsetX: 0,
      offsetY: 0,
      scale: 1
    });

    function isImage(asset: IAssetViewerItem) {
      const { contentType } = asset || {};

      return contentType?.startsWith('image/') ?? false;
    }

    function isVideo(asset: IAssetViewerItem) {
      const { contentType } = asset || {};

      return contentType?.startsWith('video/') ?? false;
    }

    function handleSrc(asset: IAssetViewerItem) {
      let src: string | undefined = undefined;

      const { content, contentType, url } = asset || {};

      if (url) {
        src = url;
      } else if (content && contentType) {
        src = `data:${contentType};base64,${content}`;
      }

      return src;
    }

    const itemsList = computed(
      () =>
        props.items?.reduce<
        {
          contentType: string;
          name: string;
          src: string | undefined;
          type: 'IMAGE' | 'VIDEO';
          uid: number;
        }[]
        >((acc, currentValue) => {
          const { contentType, name, uid } = currentValue || {};

          const type: 'IMAGE' | 'VIDEO' | 'OTHER' = isImage(currentValue)
            ? 'IMAGE'
            : isVideo(currentValue)
              ? 'VIDEO'
              : 'OTHER';

          if (type !== 'OTHER') {
            const src = handleSrc(currentValue);

            acc.push({
              contentType,
              name,
              src,
              type,
              uid
            });
          }

          return acc;
        }, []) || []
    );

    const activeIndex = ref(
      Math.max(
        0,
        itemsList.value.findIndex((item) => item.uid === props.initialUid)
      )
    );

    const isSingle = computed(() => itemsList.value.length <= 1);

    const isFirst = computed(() => activeIndex.value === 0);

    const isLast = computed(
      () => activeIndex.value === itemsList.value.length - 1
    );

    const currentAsset = computed(() => itemsList.value[activeIndex.value]);

    const assetStyle = computed(() => {
      const { deg, enableTransition, offsetX, offsetY, scale } =
        transform.value;
      let translateX = offsetX / scale;
      let translateY = offsetY / scale;

      switch (deg % 360) {
        case 90:
        case -270:
          [translateX, translateY] = [translateY, -translateX];
          break;
        case 180:
        case -180:
          [translateX, translateY] = [-translateX, -translateY];
          break;
        case 270:
        case -90:
          [translateX, translateY] = [-translateY, translateX];
          break;
      }

      const style: Record<string, string> = {
        transform: `scale(${scale}) rotate(${deg}deg) translate(${translateX}px, ${translateY}px)`,
        transition: enableTransition ? 'transform .3s' : ''
      };

      if (mode.value.name === modes.CONTAIN.name) {
        style.maxWidth = style.maxHeight = '100%';
      }

      return style;
    });

    function unregisterEventListener() {
      scopeEventListener.stop();
    }

    function hide() {
      unregisterEventListener();
      emit('close');
    }

    function handleAssetLoad() {
      loading.value = false;
    }

    function handleAssetError(e: Event) {
      loading.value = false;
      (e.target as HTMLImageElement).alt = 'Error';
    }

    function handleMouseDown(e: MouseEvent) {
      if (loading.value || e.button !== 0 || !wrapper.value) {
        return;
      }

      transform.value.enableTransition = false;

      const { offsetX, offsetY } = transform.value;
      const startX = e.pageX;
      const startY = e.pageY;

      const dragHandler = throttle((ev: MouseEvent) => {
        transform.value = {
          ...transform.value,
          offsetX: offsetX + ev.pageX - startX,
          offsetY: offsetY + ev.pageY - startY
        };
      });
      const removeMousemove = useEventListener(
        document,
        'pointermove',
        dragHandler
      );
      useEventListener(document, 'pointerup', () => {
        removeMousemove();
      });

      e.preventDefault();
    }

    function reset() {
      transform.value = {
        deg: 0,
        enableTransition: false,
        offsetX: 0,
        offsetY: 0,
        scale: 1
      };
    }

    function toggleMode() {
      if (loading.value) {
        return;
      }

      const modeNames = Object.keys(modes);
      const modeValues = Object.values(modes);
      const currentMode = mode.value.name;
      const index = modeValues.findIndex((i) => i.name === currentMode);
      const nextIndex = (index + 1) % modeNames.length;
      mode.value = modes[modeNames[nextIndex] as 'CONTAIN' | 'ORIGINAL'];
      reset();
    }

    function setActiveItem(index: number) {
      const length = itemsList.value.length || 0;
      activeIndex.value = (index + length) % length;
    }

    function prev() {
      if (isFirst.value && !props.infinite) {
        return;
      }

      setActiveItem(activeIndex.value - 1);
    }

    function next() {
      if (isLast.value && !props.infinite) {
        return;
      }

      setActiveItem(activeIndex.value + 1);
    }

    function handleActions(action: AssetViewerAction, options = {}) {
      if (loading.value) {
        return;
      }

      if (action === 'download') {
        const { name, src } = currentAsset.value || {};

        if (src) {
          downloadAssetFromUrl(src, name);
        }
      } else {
        const { enableTransition, rotateDeg, zoomRate } = {
          enableTransition: true,
          rotateDeg: 90,
          zoomRate: props.zoomRate,
          ...options
        };

        switch (action) {
          case 'zoomOut':
            if (transform.value.scale > 0.2) {
              transform.value.scale = Number.parseFloat(
                (transform.value.scale / zoomRate).toFixed(3)
              );
            }
            break;
          case 'zoomIn':
            if (transform.value.scale < 7) {
              transform.value.scale = Number.parseFloat(
                (transform.value.scale * zoomRate).toFixed(3)
              );
            }
            break;
          case 'clockwise':
            transform.value.deg += rotateDeg;
            break;
          case 'anticlockwise':
            transform.value.deg -= rotateDeg;
            break;
        }

        transform.value.enableTransition = enableTransition;
      }
    }

    function registerEventListener() {
      const keydownHandler = throttle((e: KeyboardEvent) => {
        switch (e.code) {
          // ESC
          case EVENT_CODE.esc:
            props.closeOnPressEscape && hide();
            break;
          // SPACE
          case EVENT_CODE.space:
            toggleMode();
            break;
          // LEFT_ARROW
          case EVENT_CODE.left:
            prev();
            break;
          // UP_ARROW
          case EVENT_CODE.up:
            handleActions('zoomIn');
            break;
          // RIGHT_ARROW
          case EVENT_CODE.right:
            next();
            break;
          // DOWN_ARROW
          case EVENT_CODE.down:
            handleActions('zoomOut');
            break;
        }
      });
      const mousewheelHandler = throttle((e: WheelEvent) => {
        const delta = e.deltaY || e.deltaX;
        handleActions(delta < 0 ? 'zoomIn' : 'zoomOut', {
          enableTransition: false,
          zoomRate: props.zoomRate
        });
      });

      scopeEventListener.run(() => {
        useEventListener(document, 'keydown', keydownHandler);
        useEventListener(document, 'wheel', mousewheelHandler);
      });
    }

    watch(currentAsset, () => {
      if (currentAsset.value?.type === 'IMAGE') {
        nextTick(() => {
          const asset = assetRefs.value[activeIndex.value];

          if (!(asset as HTMLImageElement)?.complete) {
            loading.value = true;
          }
        });
      }
    });

    watch(activeIndex, (val, oldVal) => {
      if (
        typeof oldVal === 'number' &&
        assetRefs.value[oldVal] instanceof HTMLVideoElement
      ) {
        try {
          (assetRefs.value[oldVal] as HTMLVideoElement).pause();
        } catch (error) {}
      }

      reset();
      emit('switch', itemsList.value?.[val]?.uid);
    });

    onMounted(() => {
      registerEventListener();
      // add tabindex then wrapper can be focusable via Javascript
      // focus wrapper so arrow key can't cause inner scroll behavior underneath
      wrapper.value?.focus?.();
    });

    return {
      activeIndex,
      assetRefs,
      assetStyle,
      handleActions,
      handleAssetError,
      handleAssetLoad,
      handleMouseDown,
      hide,
      isFirst,
      isLast,
      isSingle,
      itemsList,
      mode,
      next,
      prev,
      toggleMode,
      wrapper
    };
  }
});
